import { normalizePlayerName } from '@secretnenshi/shared';

// Spec §59: the reconnect token lives under
//   secret-nenshi:{gameCode}:{playerName}:token
// Storage can be unavailable (private mode); every access is guarded.

const PREFIX = 'secret-nenshi:';
const TOKEN_KEY = /^secret-nenshi:(\d{6}):(.+):token$/;
const CURRENT_KEY = `${PREFIX}current`;
const DISCLAIMER_KEY = `${PREFIX}disclaimer-seen`;
const THEME_KEY = `${PREFIX}theme`;

export interface SavedSeat {
  code: string;
  name: string;
  token: string;
}

function local(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function session(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

export function tokenKey(code: string, name: string): string {
  return `${PREFIX}${code}:${name}:token`;
}

export function listSavedSeats(): SavedSeat[] {
  const store = local();
  if (!store) return [];
  return safe(() => {
    const seats: SavedSeat[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      const m = key && TOKEN_KEY.exec(key);
      const token = m && store.getItem(key!);
      if (m && token) seats.push({ code: m[1]!, name: m[2]!, token });
    }
    return seats;
  }, []);
}

/** Matches names the way the server does, so "mo" finds the seat saved as "Mo". */
export function findSavedSeat(code: string, name: string): SavedSeat | undefined {
  const wanted = normalizePlayerName(name);
  return listSavedSeats().find((s) => s.code === code && normalizePlayerName(s.name) === wanted);
}

export function saveSeat(code: string, name: string, token: string): void {
  forgetSeat(code, name);
  safe(() => local()?.setItem(tokenKey(code, name), token), undefined);
}

export function forgetSeat(code: string, name: string): void {
  const existing = findSavedSeat(code, name);
  safe(() => {
    if (existing) local()?.removeItem(tokenKey(existing.code, existing.name));
    local()?.removeItem(tokenKey(code, name));
  }, undefined);
}

/** The seat this tab is using, so a page reload rejoins automatically. */
export function getCurrentSeat(): { code: string; name: string } | null {
  return safe(() => {
    const raw = session()?.getItem(CURRENT_KEY);
    return raw ? (JSON.parse(raw) as { code: string; name: string }) : null;
  }, null);
}

export function setCurrentSeat(seat: { code: string; name: string } | null): void {
  safe(() => {
    if (seat) session()?.setItem(CURRENT_KEY, JSON.stringify(seat));
    else session()?.removeItem(CURRENT_KEY);
  }, undefined);
}

export function hasSeenDisclaimer(): boolean {
  return safe(() => local()?.getItem(DISCLAIMER_KEY) === '1', false);
}

export function markDisclaimerSeen(): void {
  safe(() => local()?.setItem(DISCLAIMER_KEY, '1'), undefined);
}

/** The game variant last chosen on the main menu. */
export function getChosenTheme(): string | null {
  return safe(() => local()?.getItem(THEME_KEY) ?? null, null);
}

export function setChosenTheme(id: string): void {
  safe(() => local()?.setItem(THEME_KEY, id), undefined);
}
